import React, { Component } from 'react';
import { connect } from 'react-redux';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Card } from 'primereact/card';
import { Toast } from 'primereact/toast';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { Dialog } from 'primereact/dialog';
import { Chart } from 'primereact/chart';
import { InputTextarea } from 'primereact/inputtextarea';
import { Tag } from 'primereact/tag';
import { Calendar } from 'primereact/calendar';
import { baseUrlAdmin } from '../../store/apiConstants';
import Service from '../../services';
import LoadingComponent from '../loadingComponent';
import Authorizer from '../session/authorizer';
import { PERMISSIONS } from '../../session/authorize';
import './RiskOverview.scss';

class RiskOverview extends Component {
    constructor(props) {
        super(props);
        this.service = new Service();
        
        this.state = {
            loading: false,
            riskData: [],
            configWeights: null, // Store configured weights for display
            globalFilter: '',
            selectedRiskBand: null,
            expandedRows: null,
            
            // Chart Data
            chartData: {},
            chartOptions: {},
            
            // Override Dialog
            showOverrideDialog: false,
            selectedStudentForOverride: null,
            overrideBand: null,
            overrideJustification: '',
            overrideUntil: null,
            overrideDays: '',
            overrideSaving: false
        };
        
        this.toast = React.createRef();
        this.dt = React.createRef();
        
        this.riskBands = [
            { label: 'Critical Risk', value: 'CRITICAL' },
            { label: 'High Risk', value: 'HIGH' },
            { label: 'Medium Risk', value: 'MEDIUM' },
            { label: 'Low Risk', value: 'LOW' }
        ];

        this.riskBandOptions = [
            { label: 'Do Not Escalate (Force Low)', value: 'LOW' },
            { label: 'Treat as Medium', value: 'MEDIUM' },
            { label: 'Treat as High', value: 'HIGH' },
            { label: 'Treat as Critical', value: 'CRITICAL' }
        ];
    }

    componentDidMount() {
        this.fetchRiskData();
    }

    fetchRiskData = async () => {
        this.setState({ loading: true });
        try {
            const academicYear = localStorage.getItem('userAcademicYear');
            
            // orgId is now consistently retrieved from req.user.orgId on the backend
            const url = `${baseUrlAdmin}/fee-decision/risk/overview?academicYear=${academicYear}`;
            
            const response = await this.service.get(url, true);
            
            if (response && response.status) {
                const data = response.res?.data || [];
                const config = response.res?.config || null;
                
                // Sort by riskScore desc by default
                data.sort((a, b) => b.riskScore - a.riskScore);
                
                this.setState({ 
                    riskData: data,
                    configWeights: config?.weights || { paymentDelay: 40, brokenPromise: 20, currentOverdue: 10, discountStress: 10, engagementPenalty: 20 }
                }, this.initChart);
            } else {
                this.showToast('error', 'Error', 'Failed to fetch risk analysis');
            }
        } catch (error) {
            console.error('Error fetching risk data:', error);
            this.showToast('error', 'Error', 'Failed to fetch risk analysis');
        } finally {
            this.setState({ loading: false });
        }
    };

    initChart = () => {
        const { riskData } = this.state;
        const counts = { CRITICAL: 0, HIGH: 0, MEDIUM: 0, LOW: 0 };
        
        riskData.forEach(d => {
            if (counts[d.riskBand] !== undefined) counts[d.riskBand]++;
        });

        const chartData = {
            labels: ['Critical', 'High', 'Medium', 'Low'],
            datasets: [
                {
                    data: [counts.CRITICAL, counts.HIGH, counts.MEDIUM, counts.LOW],
                    backgroundColor: ['#EF4444', '#F97316', '#EAB308', '#22C55E'],
                    hoverBackgroundColor: ['#DC2626', '#EA580C', '#CA8A04', '#16A34A']
                }
            ]
        };

        const chartOptions = {
            plugins: {
                legend: { position: 'bottom' }
            }
        };

        this.setState({ chartData, chartOptions });
    }

    openOverrideDialog = (student) => {
        this.setState({
            selectedStudentForOverride: student,
            overrideBand: null, // Reset or set to current
            overrideJustification: '',
            overrideUntil: student?.overrideUntil ? new Date(student.overrideUntil) : null,
            overrideDays: '',
            showOverrideDialog: true
        });
    }

    saveOverride = async () => {
        const { selectedStudentForOverride, overrideBand, overrideJustification, overrideUntil, overrideDays } = this.state;
        if (!overrideBand || !overrideJustification || (!overrideUntil && !overrideDays)) {
            this.showToast('warn', 'Validation', 'Please select a band, provide justification, and set override duration.');
            return;
        }

        this.setState({ loading: true });
        try {
            const academicYear = localStorage.getItem('userAcademicYear');
            let orgId = this.props.userInfo?.orgId || JSON.parse(localStorage.getItem('userInfo'))?.orgId;
            
            // Calculate override until date if using days
            let overrideUntilDate = overrideUntil;
            if (!overrideUntilDate && overrideDays) {
                const days = parseInt(overrideDays, 10);
                if (!isNaN(days) && days > 0) {
                    const date = new Date();
                    date.setHours(0, 0, 0, 0);
                    date.setDate(date.getDate() + days);
                    overrideUntilDate = date.toISOString();
                }
            }
            
            const body = {
                orgId,
                academicYear,
                studentId: selectedStudentForOverride.studentId,
                overrideBand,
                justification: overrideJustification,
                overrideUntil: overrideUntilDate
            };

            // Use new approval workflow endpoint
            const url = `${baseUrlAdmin}/fee-decision/governance/overrides/request`;
            const response = await this.service.post(url, body, true);

            if (response && response.status && response.res?.success) {
                const requiresApproval = response.res.requiresApproval;
                const message = requiresApproval 
                    ? 'Override request submitted for approval' 
                    : 'Risk Override Applied Successfully';
                const severity = requiresApproval ? 'info' : 'success';
                
                this.showToast(severity, 'Success', message);
                this.setState({ showOverrideDialog: false });
                this.fetchRiskData(); // Refresh list
            } else {
                this.showToast('error', 'Error', 'Failed to create override request');
            }
        } catch (error) {
            console.error(error);
            this.showToast('error', 'Error', 'Failed to create override request');
        } finally {
            this.setState({ loading: false });
        }
    }

    showToast = (severity, summary, detail) => {
        this.toast.current?.show({ severity, summary, detail, life: 3000 });
    };

    exportCSV = () => {
        this.dt.current.exportCSV();
    };

    professionBody = (rowData) => {
        const profession = rowData.professionContext?.category || rowData.profession || 'UNKNOWN';
        let severity = 'info';
        
        switch (profession) {
            case 'SALARIED': severity = 'success'; break;
            case 'BUSINESS': severity = 'warning'; break;
            case 'FARMER': severity = 'info'; break;
            case 'DAILY_WAGE': severity = 'danger'; break;
            default: severity = 'secondary';
        }
        
        return <Tag severity={severity} value={profession} />;
    }

    renderDate = (dateString) => {
        if (!dateString) return '-';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-GB'); // DD/MM/YYYY
    }

    riskBandBody = (rowData) => {
        const band = rowData.riskBand;
        
        let severity = null;
        let style = null;
        
        if (band === 'CRITICAL') style = { backgroundColor: '#FFA6BC', color: '#BF0031' };
        else if (band === 'HIGH') style = { backgroundColor: '#FFEDD5', color: '#C25400' };
        else if (band === 'LOW') style = { backgroundColor: '#DCFCE7', color: '#268900' };
        else if (band === 'MEDIUM') {
             style = { backgroundColor: '#FEF9C3', color: '#854D0E' }; // Explicit Yellow
        } else {
             style = { backgroundColor: '#E6E8EA', color: '#4B5563' };
        }

        return (
            <div className="flex align-items-center gap-2">
                <Tag value={band} severity={severity} style={style} />
                
                {rowData.isOverridden && <i className="pi pi-user-edit text-orange-500" title="Manually Overridden"></i>}
                {rowData.hasPendingOverride && (
                    <Tag 
                        severity="warning" 
                        value="PENDING APPROVAL" 
                        icon="pi pi-clock"
                        className="text-xs"
                        title={`Override request to ${rowData.pendingOverrideDetails?.requestedBand} by ${rowData.pendingOverrideDetails?.requestedBy || 'Unknown'}`}
                    />
                )}
            </div>
        );
    };

    scoreBody = (rowData) => {
        let color = '#16A34A'; // Low (Green)
        if (rowData.riskScore >= 75) color = '#DC2626'; // Critical (Red)
        else if (rowData.riskScore >= 50) color = '#C2410C'; // High (Deep Orange)
        else if (rowData.riskScore >= 25) color = '#EAB308'; // Medium (Yellow)

        return <span className="font-bold text-lg" style={{ color }}>{rowData.riskScore}</span>;
    };
    
    currencyBody = (rowData) => {
        return rowData.outstandingAmount ? `₹${rowData.outstandingAmount.toLocaleString('en-IN')}` : '-';
    };

    rowExpansionTemplate = (data) => {
        const bd = data.breakdown || {};
        const weights = this.state.configWeights || { paymentDelay: 40, brokenPromise: 20, currentOverdue: 10, discountStress: 10, engagementPenalty: 20 };
        
        return (
            <div className="p-3 bg-bluegray-50 border-round">
                <div className="flex justify-content-between">
                    <div>
                        <h5>Risk Analysis & Explainability</h5>
                        <p className="text-700 italic border-left-3 border-primary pl-2 line-height-3" style={{maxWidth: '600px'}}>
                            "{data.riskNarrative || 'No detailed analysis available.'}"
                        </p>
                    </div>
                     <div className="text-right">
                         <Authorizer permId={PERMISSIONS.FDE_SET_RISK_OVERRIDE}>
                            <Button 
                                label="Override / Flag Risk" 
                                icon="pi pi-flag" 
                                className="p-button-outlined p-button-secondary p-button-sm" 
                                onClick={() => this.openOverrideDialog(data)} 
                            />
                         </Authorizer>
                     </div>
                </div>
                
                <h6 className="mt-3">Score Contribution Breakdown</h6>
                <div className="flex flex-wrap gap-4 mt-2">
                    <div className="surface-card p-3 border-round shadow-1 flex-1 text-center">
                        <div className="text-500 text-sm mb-1">Delay History</div>
                        <div className="text-xl font-bold text-primary">{bd.delay || 0}<span className="text-xs text-400">/{weights.paymentDelay}</span></div>
                    </div>
                    <div className="surface-card p-3 border-round shadow-1 flex-1 text-center">
                        <div className="text-500 text-sm mb-1">Overdue Impact</div>
                        <div className="text-xl font-bold text-orange-500">{bd.overdue || 0}<span className="text-xs text-400">/{weights.currentOverdue}</span></div>
                    </div>
                    <div className="surface-card p-3 border-round shadow-1 flex-1 text-center">
                        <div className="text-500 text-sm mb-1">Broken Promise</div>
                        <div className="text-xl font-bold text-pink-500">{bd.promise || 0}<span className="text-xs text-400">/{weights.brokenPromise}</span></div>
                    </div>
                    <div className="surface-card p-3 border-round shadow-1 flex-1 text-center">
                        <div className="text-500 text-sm mb-1">Discount Stress</div>
                        <div className="text-xl font-bold text-purple-500">{bd.discount || 0}<span className="text-xs text-400">/{weights.discountStress}</span></div>
                    </div>
                     <div className="surface-card p-3 border-round shadow-1 flex-1 text-center">
                        <div className="text-500 text-sm mb-1">Engagement</div>
                        <div className="text-xl font-bold text-blue-500">{bd.engagement || 0}<span className="text-xs text-400">/{weights.engagementPenalty}</span></div>
                    </div>
                </div>
                
                {data.hasPendingOverride && (
                     <div className="mt-3 bg-orange-50 p-3 border-round border-1 border-orange-300">
                         <div className="flex align-items-center mb-2">
                             <i className="pi pi-clock mr-2 text-orange-600 text-xl"></i>
                             <span className="font-bold text-orange-700 text-lg">Override Request Pending Approval</span>
                         </div>
                         <div className="grid text-sm">
                             <div className="col-3"><span className="text-600">Requested Band:</span></div>
                             <div className="col-9">
                                 {this.riskBandBody({ riskBand: data.pendingOverrideDetails?.requestedBand, isOverridden: false, hasPendingOverride: false })}
                             </div>
                             <div className="col-3"><span className="text-600">Requested By:</span></div>
                             <div className="col-9 font-semibold">{data.pendingOverrideDetails?.requestedBy || 'Unknown'}</div>
                             <div className="col-3"><span className="text-600">Requested On:</span></div>
                             <div className="col-9">{this.renderDate(data.pendingOverrideDetails?.requestedAt)}</div>
                             <div className="col-3"><span className="text-600">Justification:</span></div>
                             <div className="col-9 text-700">{data.pendingOverrideDetails?.justification || '-'}</div>
                         </div>
                     </div>
                )}
                
                {data.isOverridden && (
                     <div className="mt-3 bg-yellow-50 p-2 border-round border-1 border-yellow-200 text-sm">
                         <i className="pi pi-info-circle mr-2 text-yellow-600"></i>
                         <span className="font-bold">Overridden by Management:</span> {data.overrideJustification}
                         {data.overrideUntil && (
                             <span className="ml-2 text-700">(Till {new Date(data.overrideUntil).toLocaleDateString()})</span>
                         )}
                     </div>
                )}
            </div>
        );
    };

    renderHeader = () => {
        const { globalFilter, selectedRiskBand } = this.state;
        return (
            <div className="flex justify-content-between align-items-center flex-wrap gap-2">
                <div className="flex align-items-center gap-2">
                    <span className="p-input-icon-left">
                        <i className="pi pi-search" />
                        <InputText 
                            type="search" 
                            value={globalFilter} 
                            onChange={(e) => this.setState({ globalFilter: e.target.value })} 
                            placeholder="Search Student..." 
                        />
                    </span>
                    <Dropdown 
                        value={selectedRiskBand} 
                        options={this.riskBands} 
                        onChange={(e) => this.setState({ selectedRiskBand: e.value })} 
                        placeholder="Filter by Risk Band" 
                        showClear
                        style={{ width: '200px' }}
                    />
                </div>
                <div className="flex gap-2">
                    <Authorizer permId={PERMISSIONS.FDE_RISK_CONFIGURATION}>
                        <Button 
                            type="button" 
                            icon="pi pi-cog" 
                            label="Risk Config"
                            className="p-button-secondary p-button-outlined" 
                            onClick={() => window.location.href = '/financeNew/RiskConfiguration'}
                            tooltip="Configure Scoring Rules"
                        />
                    </Authorizer>
                    <Authorizer permId={PERMISSIONS.FDE_EXPORT_RISK_REPORT}>
                        <Button 
                            type="button" 
                            icon="pi pi-file-excel" 
                            className="p-button-success p-button-outlined" 
                            onClick={this.exportCSV} 
                            data-pr-tooltip="Export to Excel" 
                            tooltip="Export to Excel"
                            tooltipOptions={{position: 'bottom'}}
                        />
                    </Authorizer>
                </div>
            </div>
        );
    };
    
    getFilteredData = () => {
        const { riskData, selectedRiskBand } = this.state;
        if (!selectedRiskBand) return riskData;
        return riskData.filter(item => item.riskBand === selectedRiskBand);
    };

    render() {
        const { loading, expandedRows, chartData, chartOptions, showOverrideDialog, selectedStudentForOverride, overrideSaving } = this.state;
        const filteredData = this.getFilteredData();
        const header = this.renderHeader();
        
        // Metrics for cards
        const criticalCount = filteredData.filter(d => d.riskBand === 'CRITICAL').length;
        const avgScore = filteredData.length > 0 
            ? (filteredData.reduce((acc, curr) => acc + curr.riskScore, 0) / filteredData.length).toFixed(1)
            : 0;

        return (
            <div className="risk-overview-container">
                <Toast ref={this.toast} position="bottom-right" />
                
                <div className="grid mb-3">
                    <div className="col-12 md:col-8">
                         <div className="card h-full flex align-items-center justify-content-around">
                             <div className="text-center">
                                 <h2 className="text-red-500 m-0">{criticalCount}</h2>
                                 <div className="text-500">Critical Cases</div>
                             </div>
                             <div className="text-center">
                                 <h2 className="text-primary m-0">{avgScore}</h2>
                                 <div className="text-500">Avg Risk Score</div>
                             </div>
                             <div style={{ width: '250px', height: '150px' }}>
                                 {chartData.datasets && <Chart type="doughnut" data={chartData} options={{...chartOptions, responsive: true, maintainAspectRatio: false}} style={{height: '100%'}} />}
                             </div>
                         </div>
                    </div>
                </div>

                <div className="card">
                    <DataTable 
                        ref={this.dt}
                        value={filteredData} 
                        paginator 
                        rows={10} 
                        header={header}
                        globalFilter={this.state.globalFilter}
                        expandedRows={expandedRows}
                        onRowToggle={(e) => this.setState({ expandedRows: e.data })}
                        rowExpansionTemplate={this.rowExpansionTemplate}
                        dataKey="studentId"
                        emptyMessage="No risk records found."
                        rowsPerPageOptions={[10, 25, 50]}
                    >
                        <Column expander style={{ width: '3em' }} />
                        <Column field="studentName" header="Student Name" sortable />
                        <Column field="className" header="Class" sortable />
                        <Column field="profession" header="Profession" body={this.professionBody} style={{ minWidth: '120px' }} />
                        <Column field="outstandingAmount" header="Balance Due" body={this.currencyBody} sortable sortField="outstandingAmount" />
                        <Column field="riskScore" header="Risk Score" body={this.scoreBody} sortable />
                        <Column field="riskBand" header="Risk Band" body={this.riskBandBody} sortable />
                        <Column field="nextAction" header="Next Best Action" sortable />
                    </DataTable>
                </div>
                
                <Dialog 
                    header="Override Risk Band" 
                    visible={showOverrideDialog} 
                    style={{ width: '400px' }} 
                    onHide={() => this.setState({ showOverrideDialog: false })}
                    footer={
                        <div>
                            <Button 
                                label="Cancel" 
                                icon="pi pi-times" 
                                onClick={() => this.setState({ showOverrideDialog: false })} 
                                className="p-button-text" 
                            />
                            <Authorizer permId={PERMISSIONS.FDE_SET_RISK_OVERRIDE}>
                                <Button 
                                    label="Save Override" 
                                    icon="pi pi-check" 
                                    onClick={this.saveOverride} 
                                    loading={overrideSaving} 
                                    autoFocus 
                                />
                            </Authorizer>
                         </div>
                    }
                >
                    <div className="flex flex-column gap-3">
                        <span className="text-sm text-gray-600">
                             Overriding risk for <strong>{selectedStudentForOverride?.studentName}</strong>. 
                                This will lock their risk band until the override end date.
                        </span>
                        <div>
                            <label className="block mb-2 font-bold">New Risk Band</label>
                            <Dropdown 
                                value={this.state.overrideBand} 
                                options={this.riskBandOptions} 
                                onChange={(e) => this.setState({ overrideBand: e.value })} 
                                placeholder="Select Band" 
                                className="w-full"
                            />
                        </div>
                        <div>
                             <label className="block mb-2 font-bold">Justification (Required)</label>
                             <InputTextarea 
                                rows={3} 
                                value={this.state.overrideJustification} 
                                onChange={(e) => this.setState({ overrideJustification: e.target.value })} 
                                placeholder="E.g., Family hardship, extension approved..."
                                className="w-full"
                             />
                        </div>
                        <div>
                            <label className="block mb-2 font-bold">Override End Date</label>
                            <Calendar
                                value={this.state.overrideUntil}
                                onChange={(e) => this.setState({ overrideUntil: e.value })}
                                className="w-full"
                                showIcon
                                minDate={new Date()}
                            />
                        </div>
                        <div>
                            <label className="block mb-2 font-bold">Override Duration (Days)</label>
                            <InputText
                                value={this.state.overrideDays}
                                onChange={(e) => {
                                    const days = e.target.value.replace(/[^0-9]/g, '');
                                    const parsed = parseInt(days, 10);
                                    let overrideUntil = this.state.overrideUntil;
                                    if (!Number.isNaN(parsed) && parsed > 0) {
                                        const target = new Date();
                                        target.setHours(0, 0, 0, 0);
                                        target.setDate(target.getDate() + parsed);
                                        overrideUntil = target;
                                    }
                                    this.setState({ overrideDays: days, overrideUntil });
                                }}
                                placeholder="E.g., 15"
                                className="w-full"
                            />
                        </div>
                    </div>
                </Dialog>

                {loading && <LoadingComponent />}
            </div>
        );
    }
}

const mapStateToProps = (state) => ({
    userInfo: state.currentUserPerms ? state.currentUserPerms.userInfo : {}
});

export default connect(mapStateToProps)(RiskOverview);
